<template>
    <div class="listener-account">
        <section class="listener-account__hero">
            <div class="listener-account__top">
                <button class="listener-account__back" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18l-6-6 6-6"></path>
                    </svg>
                </button>
                <div>
                    <div class="listener-account__eyebrow">{{ $tt('Account') }}</div>
                    <h1 class="listener-account__title">{{ $tt('个人配置') }}</h1>
                </div>
            </div>

            <div class="listener-account__summary">
                <div class="listener-account__avatar">
                    {{ usernameInitial }}
                </div>
                <div class="listener-account__summary-main">
                    <div class="listener-account__name">{{ username || $tt('当前用户') }}</div>
                    <div class="listener-account__meta">{{ roleLabel }}</div>
                    <div v-if="isAppMode && apiBaseUrl" class="listener-account__server">
                        {{ $tt('服务器') }} · {{ apiBaseUrl }}
                    </div>
                </div>
            </div>
        </section>

        <section class="listener-account__section">
            <div class="listener-account__section-header">
                <h2>{{ $tt('语言') }}</h2>
                <span>{{ currentLocaleLabel }}</span>
            </div>
            <div class="listener-account__locale-list">
                <button
                    v-for="item in languageOptions"
                    :key="item.value"
                    :class="['listener-account__locale-pill', currentLocale === item.value ? 'is-active' : '']"
                    @click="setLocale(item.value)"
                >
                    {{ item.label }}
                </button>
            </div>
        </section>

        <section class="listener-account__section">
            <div class="listener-account__section-header">
                <h2>{{ $tt('播放偏好') }}</h2>
                <span>{{ $tt('本地保存') }}</span>
            </div>
            <button class="listener-account__setting-row" @click="toggleRepeatSetting">
                <div>
                    <div class="listener-account__setting-title">{{ $tt('列表循环') }}</div>
                    <div class="listener-account__setting-sub">{{ $tt('播放结束后继续从队列中循环。') }}</div>
                </div>
                <span :class="['listener-account__switch', repeatEnabled ? 'is-active' : '']"></span>
            </button>
            <button class="listener-account__setting-row" @click="toggleShuffleSetting">
                <div>
                    <div class="listener-account__setting-title">{{ $tt('随机播放') }}</div>
                    <div class="listener-account__setting-sub">{{ $tt('下一首按随机顺序选择。') }}</div>
                </div>
                <span :class="['listener-account__switch', shuffleEnabled ? 'is-active' : '']"></span>
            </button>
        </section>

        <section v-if="isAppMode" class="listener-account__section">
            <div class="listener-account__section-header">
                <h2>{{ $tt('连接') }}</h2>
                <span>{{ $tt('应用模式') }}</span>
            </div>
            <button class="listener-account__action listener-account__action--soft" @click="switchServer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 7h16M4 12h16M4 17h16"></path>
                </svg>
                <span>{{ $tt('切换服务器') }}</span>
            </button>
        </section>

        <section class="listener-account__section listener-account__section--actions">
            <div class="listener-account__section-header">
                <h2>{{ $tt('账户') }}</h2>
                <span>{{ $tt('安全') }}</span>
            </div>
            <button class="listener-account__action listener-account__action--danger" @click="logout">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <path d="M16 17l5-5-5-5"></path>
                    <path d="M21 12H9"></path>
                </svg>
                <span>{{ $tt('退出登录') }}</span>
            </button>
        </section>
    </div>
</template>

<script>
import { getApiBaseUrl, isAppMode } from '@/utils/apiConfig'

export default {
    name: 'ListenerAccountPage',
    computed: {
        username() {
            return this.$store.state.common.username
        },
        userRole() {
            return this.$store.state.common.userRole
        },
        repeatEnabled() {
            return this.$store.state.common.repeat
        },
        shuffleEnabled() {
            return this.$store.state.common.shuffle
        },
        usernameInitial() {
            return (this.username || 'U').slice(0, 1).toUpperCase()
        },
        roleLabel() {
            if (this.userRole === 'V1') {
                return this.$tt('控制台用户')
            }
            if (this.$store.state.common.isSuperuser) {
                return this.$tt('收藏家')
            }
            return this.$tt('听众')
        },
        languageOptions() {
            return [
                { value: 'zh-CN', label: this.$tt('简体中文') },
                { value: 'zh-TW', label: this.$tt('繁體中文') },
                { value: 'en-US', label: 'English' }
            ]
        },
        currentLocale() {
            return this.$localeState.locale
        },
        currentLocaleLabel() {
            const matched = this.languageOptions.find(item => item.value === this.currentLocale)
            return matched ? matched.label : this.currentLocale
        },
        isAppMode() {
            return isAppMode()
        },
        apiBaseUrl() {
            return getApiBaseUrl()
        }
    },
    methods: {
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/discover' })
        },
        setLocale(locale) {
            if (locale === this.currentLocale) {
                return
            }
            const targetLocale = this.$setLocale(locale)
            const localeName = this.languageOptions.find(item => item.value === targetLocale)?.label || targetLocale
            this.$myMsg.notify({
                title: this.$tt('语言已更新'),
                content: this.$tt('已切换为 {name}', { name: localeName }),
                type: 'success',
                time: 1600,
            })
        },
        toggleRepeatSetting() {
            this.$store.commit('toggleRepeat')
        },
        toggleShuffleSetting() {
            this.$store.commit('toggleShuffle')
        },
        switchServer() {
            this.clearAuthState()
            window.needsServerConfig = true
            this.$router.replace({ name: 'login' })
        },
        clearAuthState() {
            this.$store.commit('clearQueue')
            this.$store.commit('setUsername', '')
            this.$store.commit('setUserRole', '')
            this.$store.commit('setIsSuperuser', false)
            this.setCookie('AUTHORIZATION', '', -1)
            this.setCookie('username', '', -1)
            this.setCookie('salt', '', -1)
            this.setCookie('hash', '', -1)
        },
        async logout() {
            try {
                await this.$api.Task.logout()
            } catch (error) {
                console.warn('退出登录接口请求失败，已在前端清理登录态:', error)
            }
            this.clearAuthState()
            this.$router.replace({ name: 'login' })
        }
    }
}
</script>

<style scoped>
.listener-account {
    height: calc(100dvh - 12.25rem);
    padding: 0.35rem 0.3rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-account__hero {
    padding: 0.4rem 0.2rem 0.1rem;
}

.listener-account__top {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
}

.listener-account__back {
    width: 2.8rem;
    height: 2.8rem;
    flex-shrink: 0;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
}

.listener-account__back svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-account__eyebrow {
    font-size: 0.88rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-account__title {
    margin-top: 0.18rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-account__summary {
    margin-top: 1rem;
    padding: 1.1rem;
    border-radius: 1.7rem;
    background:
        radial-gradient(circle at top right, rgba(191, 219, 254, 0.36), transparent 32%),
        linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(255, 255, 255, 0.78));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
    display: flex;
    align-items: center;
    gap: 0.95rem;
}

.listener-account__avatar {
    width: 3.3rem;
    height: 3.3rem;
    border-radius: 1.2rem;
    background: #111827;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.12rem;
    font-weight: 800;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18);
}

.listener-account__summary-main {
    min-width: 0;
    flex: 1;
}

.listener-account__name {
    font-size: 1.08rem;
    font-weight: 800;
    color: #111827;
}

.listener-account__meta,
.listener-account__server {
    margin-top: 0.22rem;
    font-size: 0.8rem;
    line-height: 1.5;
    color: #64748b;
    word-break: break-all;
}

.listener-account__section {
    margin-top: 1.35rem;
    padding: 0 0.2rem;
}

.listener-account__section--actions {
    padding-bottom: 0.5rem;
}

.listener-account__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
    margin-bottom: 0.9rem;
}

.listener-account__section-header h2 {
    font-size: 1.45rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-account__section-header span {
    font-size: 0.8rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-account__locale-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem;
}

.listener-account__locale-pill {
    border: 0;
    padding: 0.8rem 1rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.74);
    color: #475569;
    font-size: 0.88rem;
    font-weight: 700;
    box-shadow: 0 10px 22px rgba(148, 163, 184, 0.1);
}

.listener-account__locale-pill.is-active {
    background: #111827;
    color: #fff;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}

.listener-account__setting-row,
.listener-account__action {
    width: 100%;
    border: 0;
    text-align: left;
    padding: 1rem 1.05rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.74);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
}

.listener-account__setting-row + .listener-account__setting-row,
.listener-account__action + .listener-account__action {
    margin-top: 0.8rem;
}

.listener-account__setting-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.listener-account__setting-title {
    font-size: 0.96rem;
    font-weight: 800;
    color: #111827;
}

.listener-account__setting-sub {
    margin-top: 0.2rem;
    font-size: 0.78rem;
    line-height: 1.5;
    color: #64748b;
}

.listener-account__switch {
    position: relative;
    width: 2.9rem;
    height: 1.7rem;
    flex-shrink: 0;
    border-radius: 999px;
    background: #cbd5e1;
    transition: background 0.2s ease;
}

.listener-account__switch::after {
    content: '';
    position: absolute;
    top: 0.18rem;
    left: 0.18rem;
    width: 1.34rem;
    height: 1.34rem;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.16);
    transition: transform 0.2s ease;
}

.listener-account__switch.is-active {
    background: #111827;
}

.listener-account__switch.is-active::after {
    transform: translateX(1.2rem);
}

.listener-account__action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    font-size: 0.96rem;
    font-weight: 800;
    color: #111827;
}

.listener-account__action svg {
    width: 1.1rem;
    height: 1.1rem;
}

.listener-account__action--soft {
    color: #111827;
}

.listener-account__action--danger {
    background: linear-gradient(135deg, #111827, #1f2937);
    color: #fff;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18);
}
</style>
